import { useMemo, useState } from 'react'
import { analyticsDate, ANALYTICS_TIME_ZONE } from '@shared/lib/analytics-period'
import {
  NavigationLink,
  DetailHero,
  Drawer,
  DrawerSection,
  DrawerTabPanel,
  DrawerTabs,
  ErrorState,
  LoadMore,
  MoneyValue,
  MoneyReconciliation,
  Skeleton,
  StatusBadge,
} from '@shared/design-system/ui'
import {
  releaseDetailForDisplay,
  formatReleaseDate,
  saleViewActionLabel,
  useWalletReleaseInfiniteQuery,
} from '@entities/wallet'
import type { ReleaseDeductionKind, ReleaseItemKind } from '@entities/wallet'
import type { MonetaryValue } from '@shared/lib/money/money'
import { negateMoney } from '@shared/lib/money/display-reconciliation'
import type { SelectedRelease } from './UpcomingReleases'
import {
  ledgerBreakdown,
  ledgerBreakdownAmount,
  ledgerBreakdownLabel,
  ledgerBreakdownMeta,
  ledgerBreakdownRow,
  page,
  releaseAdjustmentLead,
  releaseItemAside,
  releaseItemMeta,
  releaseItemRow,
  releaseItems,
} from './wallet.css'

const tabs = [
  { id: 'summary', label: 'Resumo' },
  { id: 'items', label: 'Itens' },
  { id: 'changes', label: 'Alterações' },
]

export function ReleaseDetailDrawer({
  release,
  onClose,
  onBack,
  onViewSale,
}: {
  release: SelectedRelease | null
  onClose: () => void
  onBack?: () => void
  onViewSale: (saleId: string) => void
}) {
  const [tab, setTab] = useState('summary')
  const query = useWalletReleaseInfiniteQuery(release?.date ?? null, release?.currency ?? null)
  const data = query.data?.pages[0]
  const items = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data])
  const displayData = useMemo(
    () =>
      data
        ? releaseDetailForDisplay(data, items, {
            itemsComplete: query.hasNextPage === false,
          })
        : undefined,
    [data, items, query.hasNextPage],
  )

  return (
    <Drawer
      open={release !== null}
      onClose={() => {
        setTab('summary')
        onClose()
      }}
      onBack={onBack}
      backLabel="Voltar para a agenda"
      title={release ? `Liberação de ${formatReleaseDate(release.date)}` : 'Liberação'}
      description="Confira o valor atual, os lançamentos e tudo o que alterou a composição."
      size="wide"
      variant="financial"
      tabs={
        <DrawerTabs tabs={tabs} value={tab} onChange={setTab} ariaLabel="Detalhe da liberação" />
      }
    >
      {query.isPending ? (
        <Skeleton height={360} />
      ) : query.isError || !displayData ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : (
        <div className={page}>
          <DetailHero
            eyebrow="Valor líquido previsto"
            value={
              <MoneyValue
                amount={displayData.summary.netRelease.amount}
                currency={displayData.summary.netRelease.currency}
                size="xl"
              />
            }
            description={`${displayData.summary.entryCount} lançamentos · calculado em ${formatDateTime(displayData.computedAt)}`}
            aside={<StatusBadge label="Prevista" tone="awaiting" />}
          />

          <DrawerTabPanel id="summary" active={tab === 'summary'}>
            <DrawerSection title="Como o valor foi formado">
              <div className={ledgerBreakdown}>
                <BreakdownRow
                  label="Volume bruto das vendas"
                  value={displayData.summary.grossVolume}
                />
                <BreakdownRow
                  label="Créditos antes dos abatimentos"
                  value={displayData.summary.originalProjected}
                />
                {displayData.summary.deductions.fxLosses ? (
                  <BreakdownRow
                    label="Perdas cambiais"
                    value={displayData.summary.deductions.fxLosses}
                    negative
                  />
                ) : null}
                <BreakdownRow
                  label="Estornos"
                  value={displayData.summary.deductions.refunds}
                  negative
                />
                <BreakdownRow
                  label="Prevenções de chargeback"
                  value={displayData.summary.deductions.preventedRefunds}
                  negative
                />
                <BreakdownRow
                  label="Chargebacks"
                  value={displayData.summary.deductions.chargebacks}
                  negative
                />
                <BreakdownRow
                  label="Multas de chargeback"
                  value={displayData.summary.deductions.chargebackFees}
                  negative
                />
                <BreakdownRow
                  label="Valor líquido previsto"
                  value={displayData.summary.netRelease}
                  strong
                />
                <MoneyReconciliation
                  total={displayData.summary.netRelease}
                  parts={[
                    displayData.summary.originalProjected,
                    ...Object.values(displayData.summary.deductions)
                      .filter((value): value is NonNullable<typeof value> => value !== undefined)
                      .map(negateMoney),
                  ]}
                />
              </div>
            </DrawerSection>
            <DrawerSection title="De onde vem">
              <div className={ledgerBreakdown}>
                <SourceRow label="Vendas avulsas" source={displayData.sources.oneTime} />
                <SourceRow
                  label="Primeiras cobranças"
                  source={displayData.sources.subscriptionInitial}
                />
                <SourceRow label="Renovações" source={displayData.sources.renewals} />
                <SourceRow label="Reservas liberadas" source={displayData.sources.reserves} />
                {displayData.sources.referrals.count > 0 ? (
                  <div data-testid="release-referral-source">
                    <SourceRow label="Comissões de indicação" source={displayData.sources.referrals} />
                  </div>
                ) : null}
                <MoneyReconciliation
                  total={displayData.summary.netRelease}
                  parts={Object.values(displayData.sources).map((source) => source.amount)}
                  complete={query.hasNextPage === false}
                />
              </div>
            </DrawerSection>
          </DrawerTabPanel>

          <DrawerTabPanel id="items" active={tab === 'items'}>
            <DrawerSection
              title="Lançamentos desta liberação"
              description="Confira os recebimentos e acesse as vendas em que você participa."
            >
              <div className={releaseItems}>
                {displayData.items.map((item) => (
                  <div className={releaseItemRow} key={item.id} data-testid={`release-item-${item.id}`}>
                    <span className={releaseAdjustmentLead}>
                      <strong>{item.isReferral ? 'Comissão de indicação' : item.buyerName}</strong>
                      <span className={releaseItemMeta}>
                        {item.isReferral ? 'Indique e ganhe' : `${item.productName} · ${releaseItemLabel(item.kind)}`}
                      </span>
                      <span className={releaseItemMeta}>
                        {item.paidAt ? `Pago em ${analyticsDate(item.paidAt)}` : item.saleId}
                      </span>
                    </span>
                    <span className={releaseItemAside}>
                      <MoneyValue
                        amount={item.amount.amount}
                        currency={item.amount.currency}
                        size="sm"
                      />
                      {item.canViewSale !== false ? <NavigationLink data-testid={`release-view-sale-${item.id}`} onClick={() => onViewSale(item.saleId)}>
                        {saleViewActionLabel(item.saleId)}{' '}
                      </NavigationLink> : null}
                    </span>
                  </div>
                ))}
              </div>
              <MoneyReconciliation
                total={displayData.summary.netRelease}
                parts={items.map((item) => item.amount)}
                complete={query.hasNextPage === false}
              />
              {query.hasNextPage ? (
                <LoadMore
                  shown={items.length}
                  hasMore={query.hasNextPage}
                  loading={query.isFetchingNextPage}
                  onClick={() => void query.fetchNextPage()}
                />
              ) : null}
            </DrawerSection>
          </DrawerTabPanel>

          <DrawerTabPanel id="changes" active={tab === 'changes'}>
            <DrawerSection
              title="O que alterou o valor"
              description="Somente eventos que consumiram lançamentos desta liberação aparecem aqui."
            >
              <div className={releaseItems}>
                {displayData.adjustments.length === 0 ? (
                  <span className={releaseItemMeta}>Nenhum abatimento alterou esta liberação.</span>
                ) : (
                  displayData.adjustments.map((adjustment) => (
                    <div className={releaseItemRow} key={adjustment.id}>
                      <span className={releaseAdjustmentLead}>
                        <strong>{deductionLabel(adjustment.kind)}</strong>
                        <span className={releaseItemMeta}>Afetou {adjustment.affectedSaleId}</span>
                        <span className={releaseItemMeta}>
                          {formatDateTime(adjustment.occurredAt)}
                        </span>
                      </span>
                      <span className={releaseItemAside}>
                        <span className={ledgerBreakdownAmount} data-tone="negative">
                          −{' '}
                          <MoneyValue
                            amount={adjustment.amount.amount}
                            currency={adjustment.amount.currency}
                            size="sm"
                          />
                        </span>
                        {adjustment.canViewSale !== false ? <NavigationLink onClick={() => onViewSale(adjustment.debtSaleId)}>
                          {saleViewActionLabel(adjustment.debtSaleId)}{' '}
                        </NavigationLink> : null}
                      </span>
                    </div>
                  ))
                )}
              </div>
            </DrawerSection>
          </DrawerTabPanel>
        </div>
      )}
    </Drawer>
  )
}

function BreakdownRow({
  label,
  value,
  negative,
  strong,
}: {
  label: string
  value: MonetaryValue
  negative?: boolean
  strong?: boolean
}) {
  return (
    <div className={ledgerBreakdownRow} data-strong={strong || undefined}>
      <span className={ledgerBreakdownLabel}>{label}</span>
      <span className={ledgerBreakdownAmount} data-tone={negative ? 'negative' : undefined}>
        {negative ? '− ' : ''}
        <MoneyValue amount={value.amount} currency={value.currency} size="sm" />
      </span>
    </div>
  )
}

function SourceRow({
  label,
  source,
}: {
  label: string
  source: { count: number; amount: MonetaryValue }
}) {
  return (
    <div className={ledgerBreakdownRow}>
      <span>
        <span className={ledgerBreakdownLabel}>{label}</span>
        <span className={ledgerBreakdownMeta}>{source.count} itens</span>
      </span>
      <MoneyValue amount={source.amount.amount} currency={source.amount.currency} size="sm" />
    </div>
  )
}

function releaseItemLabel(kind: ReleaseItemKind) {
  switch (kind) {
    case 'ONE_TIME':
      return 'Venda avulsa'
    case 'SUBSCRIPTION_INITIAL':
      return 'Nova assinatura'
    case 'RENEWAL':
      return 'Renovação'
    case 'RESERVE':
      return 'Reserva liberada'
    default:
      return assertNever(kind)
  }
}

function deductionLabel(kind: ReleaseDeductionKind) {
  switch (kind) {
    case 'REFUND':
      return 'Estorno'
    case 'PREVENTED_REFUND':
      return 'Prevenção de chargeback'
    case 'CHARGEBACK':
      return 'Chargeback'
    case 'FX_LOSS':
      return 'Ajuste por perda cambial'
    case 'CHARGEBACK_FEE':
      return 'Multa de chargeback'
    default:
      return assertNever(kind)
  }
}

function assertNever(value: never): never {
  throw new Error(`Valor financeiro não suportado: ${String(value)}`)
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: ANALYTICS_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}
